import React, {useEffect, useRef, useState} from "react";
import { Paper, Grid, CircularProgress, Box } from "@mui/material";
import VOBForm from "./vob-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { showStatus } from "../../../security/state/user-slice";
import { AppDispatch } from "../../../shared/state/store";
import { getAllPatientCurrentInsurances, savePatientCurrentInsurance, getInstanceOfCare } from "../../../patient/state/patient-thunk";
import { RootState } from "../../../shared/state/root-reducer";
import { isSelfPayPayor } from "../../../shared/utils/insurance-utils";
import { setPatientCurrentInsurance } from "../../../patient/state/patient-slice";
import { emptyPatientCurrentInsurance } from "../../../patient/components/models/patient-view-model";
import EstimatesSidebar from "../shared/estimates-sidebar";
import {TakePaymentModal} from "../../../shared/components/take-payment-modal";
import {clearPaymentURLParams} from "../../../admissions-advisor/state/vob-slice";
import {PAYMENT_TYPES} from "../../../patient/components/models/payment-type";
import { patientService } from "../../../patient/services/patient-service";
import { PatientEncounter } from "../../../patient/components/models/patient-encounter";

type VOBTabProps = {
    isVobRouteReady?: boolean;
};

function VOBTab({ isVobRouteReady = true }: VOBTabProps) {
    const { patientId: urlPatientId } = useParams<{ patientId: string }>();
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();

    const selectedPatient = useSelector(
        (state: RootState) => state.patientContext.selectedPatient
    );

    const selectedPatientCurrentInsurance = useSelector(
        (state: RootState) => state.patientContext.selectedPatientCurrentInsurance
    );
    
    const selectedVob = useSelector(
        (state: RootState) => state.vobContext.selectedVob
    );
    
    const payorsMap = useSelector(
        (state: RootState) => state.adminContext.adminConfigurationContext.payersMap
    );

    const userProfile = useSelector(
        (state: RootState) => state.userContext.userProfile
    );

    const selectedEncounter = useSelector((state: RootState) => {
        return state?.patientContext.selectedEncounter;
    });


    const estimate =  useSelector((state: RootState) => {
        return state?.estimatorContext.selectedEstimate;
    });

    const [openPaymentModal, setOpenPaymentModal] = useState(false);
    const [paymentData, setPaymentData] = useState<any>(undefined);
    const isLoadingIOCRef = useRef(false);
    const lastLoadedPatientIdRef = useRef<number | undefined>(undefined);

    const handlePaymentModalCancel = () => {
        setOpenPaymentModal(false);
        dispatch(clearPaymentURLParams());
    };

    const hasCheckedPaymentUrl = useRef(false);

    useEffect(() => {
        console.log("[VOBTab] loadLatestNotManagedIOC useEffect run", {
            patientId: selectedPatient?.patientId,
            encounterId: selectedEncounter?.patientEncounterId,
            at: new Date().toISOString(),
        });

        const loadLatestNotManagedIOC = async () => {
            if (!selectedEncounter || !selectedEncounter.patientEncounterId) {
                if (isLoadingIOCRef.current) {
                    return;
                }

                const patientId = selectedPatient?.patientId;
                
                if (!patientId) {
                    return;
                }

                if (lastLoadedPatientIdRef.current === patientId) {
                    return;
                }

                isLoadingIOCRef.current = true;
                lastLoadedPatientIdRef.current = patientId;

                try {
                    const response = await patientService.getPatientInstancesOfCare({
                        patientId: patientId,
                        limit: 100,
                        offset: 0,
                    });

                    if (response.hasErrors) {
                        console.error('Error fetching patient encounters:', response.errorMessage);
                        isLoadingIOCRef.current = false;
                        lastLoadedPatientIdRef.current = undefined;
                        return;
                    }

                    const encounters = response.entity as PatientEncounter[];

                    const encountersWorkflow2 = encounters.filter(
                        (encounter) => encounter.workflow?.workflowId === 2
                    );

                    if (encountersWorkflow2.length > 0) {
                        const latestWorkflow2Ioc = [...encountersWorkflow2].sort((a, b) => {
                            const dateA = new Date(a.instanceCreateDt || 0).getTime();
                            const dateB = new Date(b.instanceCreateDt || 0).getTime();
                            if (dateB !== dateA) {
                                return dateB - dateA;
                            }
                            return (b.patientEncounterId || 0) - (a.patientEncounterId || 0);
                        })[0];

                        await dispatch(getInstanceOfCare({
                            patientId: latestWorkflow2Ioc.patientId,
                            encounterId: latestWorkflow2Ioc.patientEncounterId,
                        }));
                    }
                } catch (error) {
                    console.error('Error loading latest workflow 2 IOC:', error);
                    lastLoadedPatientIdRef.current = undefined;
                } finally {
                    isLoadingIOCRef.current = false;
                }
            }
        };

        loadLatestNotManagedIOC();

        return () => {
            console.log("[VOBTab] loadLatestNotManagedIOC useEffect cleanup (deps changed or unmount)", {
                patientId: selectedPatient?.patientId,
                encounterId: selectedEncounter?.patientEncounterId,
            });
        };
    }, [selectedPatient?.patientId, selectedEncounter?.patientEncounterId, dispatch]);

    useEffect(() => {
        console.log("[VOBTab] fetchPatientInsurances useEffect run", {
            urlPatientId,
            selectedVobPayorId: selectedVob?.payor?.payorId,
            at: new Date().toISOString(),
        });

        const fetchData = async () => {
            if (urlPatientId) {
                const result = await dispatch(getAllPatientCurrentInsurances({ patientId: +urlPatientId }));
                
                if(result.payload.length === 0) {
                    const payorId = selectedVob?.payor?.payorId;
                    const payorPlanId = selectedVob?.plan?.payorPlanId;

                    if (!payorId || !payorPlanId) {
                        dispatch(showStatus("Please set the patient's current insurance before accessing the VOB tab"));
                        navigate(`/patients/${urlPatientId}`);
                        return;
                    }

                    if (isSelfPayPayor(payorId, payorsMap)) {
                        const selfPayPayor = Array.from(payorsMap.values()).find((payor) => {
                            return payor.payorName.toLowerCase() === 'self-pay';
                        });
                        
                        if (selfPayPayor) {
                            const uninsuredInsurance = {
                                ...emptyPatientCurrentInsurance,
                                patientId: +urlPatientId,
                                payorId: selfPayPayor.payorId,
                                payorPlanId: selfPayPayor.payorPlans?.[0]?.payorPlanId || 0,
                                subscriberName: '',
                                subscriberRelation: -1,
                                memberId: '',
                                groupNumber: '',
                            };
                            
                            try {
                                await dispatch(savePatientCurrentInsurance({
                                    insurance: uninsuredInsurance
                                }));
                                
                                dispatch(setPatientCurrentInsurance(uninsuredInsurance));
                                dispatch(showStatus("Patient has been set as Uninsured"));
                                return;
                            } catch (error) {
                                dispatch(showStatus("Please set the patient's current insurance before accessing the VOB tab"));
                                navigate(`/patients/${urlPatientId}`);
                                return;
                            }
                        }
                        return;
                    }

                    const newInsurance = {
                        ...emptyPatientCurrentInsurance,
                        patientId: +urlPatientId,
                        payorId: payorId,
                        payorPlanId: payorPlanId,
                    };
                    try {
                        await dispatch(savePatientCurrentInsurance({
                            insurance: newInsurance
                        }));

                        dispatch(setPatientCurrentInsurance(newInsurance));
                        
                        dispatch(showStatus("Insurance has been set automatically"));
                        return;
                    } catch (error) {
                        dispatch(showStatus("Please set the patient's current insurance before accessing the VOB tab"));
                        navigate(`/patients/${urlPatientId}`);
                        return;
                    }
                }
            }
        };

        fetchData();

        return () => {
            console.log("[VOBTab] fetchPatientInsurances useEffect cleanup", { urlPatientId });
        };
    }, [urlPatientId, selectedVob]);

    useEffect(() => {
        console.log("[VOBTab] paymentUrl useEffect run", {
            urlPatientId,
            encounterId: selectedEncounter?.patientEncounterId,
            estimateEncounterId: estimate?.patientEncounterId,
            hasChecked: hasCheckedPaymentUrl.current,
            at: new Date().toISOString(),
        });

        if (hasCheckedPaymentUrl.current) return;

        const urlParams = new URLSearchParams(window.location.search);
        const opParam = urlParams.get('op');

        if (opParam === 'pay' && urlPatientId && (selectedEncounter?.patientEncounterId || estimate?.patientEncounterId)) {
            setPaymentData({
                patientId: urlPatientId,
                patientEncounterId: selectedEncounter?.patientEncounterId || estimate?.patientEncounterId,
                paymentType: PAYMENT_TYPES.DOWN_PAYMENT,
                paymentChannelId: userProfile.userRole.roleType === 'I' ? 5 : 3
            });
            setOpenPaymentModal(true);
            hasCheckedPaymentUrl.current = true;
        }
        return () => {
            console.log("[VOBTab] paymentUrl useEffect cleanup", { urlPatientId });
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [estimate, selectedEncounter, urlPatientId]);

    return (
        <div
            test-id="vob-tab-container"
            style={{
                display: 'flex',
                flexDirection: 'column',
                padding: 24,
                height: 'auto',
                overflow: 'visible',
                backgroundColor: '#F5F7F9',
            }}
        >
            <div test-id="vob-tab-content" style={{ display: 'flex', gap: 24 }}>
                <Paper test-id="vob-form-paper" style={{ flex: 2, padding: 24 }}>
                    {!isVobRouteReady ? (
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            minHeight={320}
                            data-testid="vob-tab-loading"
                        >
                            <CircularProgress />
                        </Box>
                    ) : (
                        <VOBForm />
                    )}
                </Paper>
                <Grid item xs={4}>
                    <Paper test-id="vob-comments-paper">
                        <div test-id="vob-comments-container" className="px-4 py-4">
                            <EstimatesSidebar
                                clientId={selectedPatient?.clientId ? Number(selectedPatient.clientId) : undefined}
                                clientEmployerId={selectedPatient?.contact?.contactIncome?.clientEmployerId}
                                payorId={selectedPatientCurrentInsurance?.payorId}
                                groupNumber={selectedPatientCurrentInsurance?.groupNumber}
                                memberId={selectedPatientCurrentInsurance?.memberId}
                            />
                        </div>
                    </Paper>
                </Grid>
                <TakePaymentModal
                    paymentData={paymentData}
                    open={openPaymentModal}
                    handleModalCancel={handlePaymentModalCancel}
                    test-id="estimate-tab-payment-modal"
                />

            </div>
        </div>
    );
}

export default VOBTab; 